"use client"

import IconClose from "@/app/(site)/_components/Atoms/Icons/IconClose"
import StoryText from "@/app/(site)/_components/Atoms/Storytext"
import ButtonPill from "@/app/(site)/_components/Molecules/ButtonPill"
import CountryPickerSelection from "@/app/(site)/_components/Organisms/Header/CountryPickerSelection"
import { Country } from "@/app/(site)/_components/Organisms/Header/CountryPickerWrapper"
import { useAnimatedModule } from "@/app/(site)/_utils/_hooks/useAnimatedModule"
import { useCloseOnEscape } from "@/app/(site)/_utils/_hooks/useCloseOnEscape"
import { useDocumentKeydown } from "@/app/(site)/_utils/_hooks/useDocumentKeydown"
import { useDocumentOverflowControl } from "@/app/(site)/_utils/_hooks/useDocumentOverflowControl"
import { useEscClose } from "@/app/(site)/_utils/_hooks/useEscClose"
import { getLangCode } from "@/app/(site)/_utils/misc"
import useEcommerceStore from "@/app/(site)/contexts/ecommerce/store"
import { CountryCode } from "@/data/country-data"
import { ShippingCountryCode } from "@/data/shipping-country-code"
import { LanguageOption } from "@/data/supported-languages"
import { ProductMetaData } from "@/lib/content-service/types/ContentServiceProduct"
import { t } from "@/translations"
import { usePathname } from "next/navigation"
import { useCallback, useState } from "react"

type CountryPickerProps = {
    countries: Country[]
    countriesWithLanguages: Record<ShippingCountryCode, LanguageOption[]>
    extractedAbsolutePath: string | null
    productHrefs?: ProductMetaData["hrefs"]
    isProductPage?: boolean
}

export default function CountryPicker({
    countries,
    countriesWithLanguages,
    extractedAbsolutePath,
    productHrefs = {},
    isProductPage = false
}: CountryPickerProps) {
    const COUNTRY_PICKER_ANIMATION_DURATION = 200
    const langCode = getLangCode(usePathname().split("/")[1]) ?? ""

    const centraSession = useEcommerceStore(state => state.centraSession)
    const setShowCountryPicker = useEcommerceStore(state => state.setShowCountryPicker)
    const showCountryPicker = useEcommerceStore(state => state.showCountryPicker)

    const [isAnimating, setIsAnimating] = useState(false)

    const handleModuleClose = useCallback(() => {
        setIsAnimating(false)

        setTimeout(() => {
            setShowCountryPicker(false)
        }, COUNTRY_PICKER_ANIMATION_DURATION)
    }, [setShowCountryPicker])

    useAnimatedModule(showCountryPicker, setIsAnimating)
    useDocumentOverflowControl(showCountryPicker)
    useEscClose(showCountryPicker, handleModuleClose)
    useCloseOnEscape(showCountryPicker, setShowCountryPicker)
    useDocumentKeydown({
        keys: ["Escape", "Backspace"],
        action: handleModuleClose,
        condition: showCountryPicker
    })

    const moduleClasses = isAnimating
        ? "opacity-100 translate-y-0 visible"
        : "opacity-0 translate-x-[50px] -z-50 invisible"

    return (
        <div
            className={`fixed inset-0 z-40 flex overflow-y-scroll backdrop-blur-lg transition-all duration-300 ${moduleClasses}`}
            style={{ WebkitOverflowScrolling: "touch" }}
        >
            <div className="flex h-[100%] w-[100%]">
                <div
                    className="md:w-[50%] lg:w-[65%]"
                    onClick={() => handleModuleClose()}
                ></div>
                <div className="relative right-0 flex w-[100%] flex-col justify-between overflow-y-auto bg-black px-16 pb-27 text-white md:w-[50%] md:p-24 md:pt-30 lg:w-[35%]">
                    <div className="mb-100 flex flex-col justify-between">
                        <div className="mb-72 flex justify-between">
                            <div className="mt-72 w-[100%] md:mt-0 md:w-[70%]">
                                <StoryText size="size2">
                                    {t(
                                        langCode,
                                        "Select your desired shipping location and language below:"
                                    )}
                                </StoryText>
                            </div>
                            <div
                                className="absolute right-0 top-0 cursor-pointer p-16 md:pt-24"
                                onClick={handleModuleClose}
                            >
                                <IconClose className="h-24 w-24 md:h-[32px] md:w-[32px]" />
                            </div>
                        </div>
                        <CountryPickerSelection
                            langCode={langCode}
                            extractedAbsolutePath={extractedAbsolutePath}
                            shippingCountry={{
                                countryName: centraSession?.metadata?.country.name ?? "",
                                countryCode:
                                    (centraSession?.metadata?.country
                                        .code as CountryCode) ?? ""
                            }}
                            countries={countries}
                            theme="dark"
                            centraSession={centraSession}
                            countriesWithLanguages={countriesWithLanguages}
                            productHrefs={productHrefs}
                            isProductPage={isProductPage}
                        />
                    </div>
                    <ButtonPill
                        className="z-40"
                        fullWidth
                        variant="primary"
                        color="white"
                        size="medium"
                        aria-label={t(langCode, "common.close")}
                        onClick={() => handleModuleClose()}
                        type="button"
                    >
                        {t(langCode, "common.close")}
                    </ButtonPill>
                </div>
            </div>
        </div>
    )
}
